import React from 'react';
import {
  sendAddToCart,
  sendInitiateCheckout,
  sendPurchase,
  sendCompleteRegistration,
  sendSearch,
  sendPageView,
  sendViewCategory,
} from './metaConversionsAPI';
import { generateEventId, getUserData } from './metaHelpers';

const useFacebookPixel = () => {
  /**
   * Dispara un evento en Facebook Pixel
   * @param {string} eventName - Nombre del evento
   * @param {object} data - Datos del evento
   * @param {string} eventId - Event ID para deduplicación
   */
  const trackPixelEvent = (eventName, data = {}, eventId = null) => {
    if (typeof window !== 'undefined' && window.fbq) {
      if (eventId) {
        // Debug log para verificar deduplicación
        console.log(`[Meta Debug] Pixel ${eventName}:`, { eventID: eventId });
        window.fbq('track', eventName, data, { eventID: eventId });
      } else {
        window.fbq('track', eventName, data);
      }
    }
  };

  /**
   * Track AddToCart - Pixel + CAPI
   */
  const trackAddToCart = async (product, user = null) => {
    const eventId = generateEventId('addtocart', product.id || product.sku || product.product_id);
    const productPrice = product.price || product.sale_price || product.cost || 0;
    const productId = product.id || product.sku || product.product_id;
    
    // Pixel
    trackPixelEvent('AddToCart', {
      content_ids: [productId],
      content_name: product.name,
      content_type: 'product',
      value: productPrice,
      currency: 'CLP',
      content_category: product.categories?.[0]?.name || 'Sin categoría',
      quantity: product.quantity || 1
    }, eventId);

    // CAPI (async, no bloquea)
    sendAddToCart({
      id: productId,
      sku: product.sku || productId,
      name: product.name,
      price: productPrice,
      quantity: product.quantity || 1,
      currency: 'CLP',
      categories: product.categories,
    }, eventId, { user: user || getUserData() }).catch(error => {
      console.error('Error enviando AddToCart a CAPI:', error);
    });
  };

  /**
   * Track InitiateCheckout - Pixel + CAPI
   */
  const trackInitiateCheckout = async (cartItems, total, user = null) => {
    const eventId = generateEventId('initiatecheckout');
    const contentIds = cartItems.map(item => item.id || item.sku || item.product_id);
    
    // Pixel
    trackPixelEvent('InitiateCheckout', {
      content_ids: contentIds,
      content_name: cartItems.map(item => item.name),
      content_type: 'product',
      value: total,
      currency: 'CLP',
      num_items: cartItems.length
    }, eventId);

    // CAPI
    sendInitiateCheckout(total, cartItems.length, contentIds, eventId, {
      currency: 'CLP',
      user: user || getUserData(),
    }).catch(error => {
      console.error('Error enviando InitiateCheckout a CAPI:', error);
    });
  };

  /**
   * Track Purchase - Pixel + CAPI (con mismo event_id para deduplicación)
   */
  const trackPurchase = async (orderData, user = null) => {
    const eventId = generateEventId('purchase', orderData.order_id || orderData.id);
    const productIds = orderData.products?.map(item => item.id || item.sku || item.product_id) || [];
    
    // Pixel
    trackPixelEvent('Purchase', {
      content_ids: productIds,
      content_name: orderData.products?.map(item => item.name),
      content_type: 'product',
      value: orderData.total,
      currency: 'CLP',
      num_items: orderData.products?.length || 0,
      order_id: orderData.order_id || orderData.id
    }, eventId);

    // CAPI (con el MISMO eventId que el Pixel para deduplicación)
    sendPurchase(orderData, eventId, {
      currency: 'CLP',
      user: user || getUserData() || orderData.user,
    }).catch(error => {
      console.error('Error enviando Purchase a CAPI:', error);
    });
  };

  /**
   * Track Purchase - Solo Pixel (DEPRECATED: usar trackPurchase para deduplicación correcta)
   * @deprecated Usar trackPurchase en su lugar para enviar Pixel + CAPI con el mismo event_id
   */
  const trackPurchasePixelOnly = (orderData) => {
    console.warn('[Meta] trackPurchasePixelOnly está deprecado. Usar trackPurchase para deduplicación correcta.');
    const eventId = generateEventId('purchase', orderData.order_id || orderData.id);
    const productIds = orderData.products?.map(item => item.id || item.sku || item.product_id) || [];
    
    // Solo Pixel
    trackPixelEvent('Purchase', {
      content_ids: productIds,
      content_name: orderData.products?.map(item => item.name),
      content_type: 'product',
      value: orderData.total,
      currency: 'CLP',
      num_items: orderData.products?.length || 0,
      order_id: orderData.order_id || orderData.id
    }, eventId);
  };

  /**
   * Track CompleteRegistration - Pixel + CAPI
   */
  const trackCompleteRegistration = async (userData) => {
    const eventId = generateEventId('registration', userData.id);
    
    // Pixel
    trackPixelEvent('CompleteRegistration', {
      content_name: 'Registro de Usuario',
      status: true,
      user_id: userData.id,
      email: userData.email
    }, eventId);

    // CAPI
    sendCompleteRegistration(userData, eventId).catch(error => {
      console.error('Error enviando CompleteRegistration a CAPI:', error);
    });
  };

  /**
   * Track Search - Pixel + CAPI
   */
  const trackSearch = async (searchTerm, user = null) => {
    const eventId = generateEventId('search');
    
    // Pixel
    trackPixelEvent('Search', {
      search_string: searchTerm
    }, eventId);

    // CAPI
    sendSearch(searchTerm, eventId, {
      user: user || getUserData(),
    }).catch(error => {
      console.error('Error enviando Search a CAPI:', error);
    });
  };

  /**
   * Track Lead - Solo Pixel (no está en la doc CAPI)
   */
  const trackLead = (email) => {
    trackPixelEvent('Lead', {
      content_name: 'Newsletter Subscription',
      email: email
    });
  };

  /**
   * Track PageView - Pixel + CAPI (con mismo event_id para deduplicación)
   * @param {object} options - Opciones del evento
   * @param {string} options.event_source_url - URL de la página
   * @param {string} options.event_id - Event ID para deduplicación (se genera si no se proporciona)
   * @param {object} options.user - Usuario logueado (opcional)
   */
  const trackPageView = async (options = {}) => {
    // Generar o usar event_id proporcionado
    const eventId = options.event_id || generateEventId('pageview');

    // Pixel: enviar PageView con event_id para deduplicación
    trackPixelEvent('PageView', {}, eventId);

    // CAPI: enviar PageView con el MISMO event_id
    sendPageView({
      event_source_url: options.event_source_url,
      event_id: eventId,
      user: options.user || getUserData(),
    }).catch(error => {
      console.error('Error enviando PageView a CAPI:', error);
    });
  };

  /**
   * Track ViewCategory - Solo CAPI (Pixel se puede disparar manualmente)
   */
  const trackViewCategory = async (categoryName, options = {}) => {
    sendViewCategory(categoryName, {
      user: options.user || getUserData(),
    }).catch(error => {
      console.error('Error enviando ViewCategory a CAPI:', error);
    });
  };

  return {
    trackAddToCart,
    trackInitiateCheckout,
    trackPurchase,
    trackPurchasePixelOnly,
    trackCompleteRegistration,
    trackSearch,
    trackLead,
    trackPageView,
    trackViewCategory,
  };
};

export default useFacebookPixel;